import React, { useState, useRef, useEffect } from 'react';
import styles from './CollapsibleCard.module.css';
import { FaChevronDown } from 'react-icons/fa';
import classNames from 'classnames';

function Collapsible({
  className,
  summary,
  children,
  subtitle,
  selection,
  onSelect,
  onDeselect,
  containedContent,
  open,
  iconAlign = 'left'
}) {
  const [selectionState, setSelectionState] = useState(open ?? false);
  const [isOpen, setIsOpen] = useState(open ?? false);
  const contentRef = useRef(null);
  const detailsRef = useRef(null);

  // Animation flags to track if the component is currently closing or expanding
  const [isClosing, setIsClosing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  
  const handleSelectionChange = (event) => {
    const newState = event.target.checked;
    setSelectionState(newState);

    if (onSelect && newState) {
      onSelect();
    }

    if (onDeselect && !newState) {
      onDeselect();
    }
  };

  const toggleCollapsible = (e) => {
    e.preventDefault();

    // Temporarily hide overflow during animation
    detailsRef.current.style.overflow = 'hidden';

    if (isClosing || !detailsRef.current.open) {
      openCollapsibleDetails();
    } else if (isExpanding || detailsRef.current.open) {
      closeCollapsibleDetails();
    }
  };

  const closeCollapsibleDetails = () => {
    setIsClosing(true);
    const startHeight = `${detailsRef.current.offsetHeight}px`;
    const endHeight = `${detailsRef.current.querySelector('summary').offsetHeight}px`;

    // Ensure the element's height is correctly set for animation
    if (detailsRef.current.style.height) detailsRef.current.style.height = startHeight;

    // Animate the height from the current size to the summary's height (collapsed state)
    const newAnimation = detailsRef.current.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 300,
      easing: 'ease-in-out'
    });

    // When the animation finishes, reset the state and overflow
    newAnimation.onfinish = () => onAnimationFinish(false);
    newAnimation.oncancel = () => setIsClosing(false);
  };

  const openCollapsibleDetails = () => {
    // Fix the current height before starting the animation
    detailsRef.current.style.height = `${detailsRef.current.offsetHeight}px`;
    detailsRef.current.open = true;

    // Request animation frame to ensure smooth transition
    window.requestAnimationFrame(() => expandCollapsibleDetails());
  };

  const expandCollapsibleDetails = () => {
    setIsExpanding(true);
    const startHeight = `${detailsRef.current.offsetHeight}px`;
    const endHeight = `${detailsRef.current.querySelector('summary').offsetHeight + contentRef.current.offsetHeight}px`;

    // Ensure the element's height is correctly set for animation
    if (detailsRef.current.style.height) detailsRef.current.style.height = startHeight;

    // Animate the height from the current size to the full expanded size
    const newAnimation = detailsRef.current.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 300,
      easing: 'ease-in-out'
    });

    // When the animation finishes, update the state and cleanup
    newAnimation.onfinish = () => onAnimationFinish(true);
    newAnimation.oncancel = () => setIsExpanding(false);
  };

  const onAnimationFinish = (openState) => {
    // Set the open state of the details element and clear the animation flags
    detailsRef.current.open = openState;
    setIsClosing(false);
    setIsExpanding(false);
    setIsOpen(openState);

    // Reset the height and overflow to allow content to adjust naturally
    detailsRef.current.style.height = detailsRef.current.style.overflow = '';
  };

  // Syncs the external `open` prop with the internal `isOpen` state when it changes
  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  return (
    <div className={className}>
      <details 
        ref={detailsRef} 
        className={classNames(
          containedContent ? styles.detailsContained : styles.details
        )} 
        open={isOpen}
      >
        <summary 
          className={classNames(styles.summary, { "flex-row-reverse": iconAlign === 'right' })} 
          onClick={toggleCollapsible}
        >
          {selection ? (
            <input
              className={styles.CheckBox}
              checked={selectionState}
              onChange={handleSelectionChange}
              type="checkbox"
            />
          ) : (
            <FaChevronDown 
              className={styles.ArrowIcon}
              style={{
                transform: isExpanding ? 'rotate(0deg)' : (isClosing ? 'rotate(180deg)' : ''),
                transition: 'transform 300ms ease-in-out',
              }}
            />
          )}
          <span className={styles.title}>{summary}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </summary>
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
      </details>
    </div>
  );
}

export default Collapsible;
